import { useQuery } from "@apollo/client";
import { compact, take, uniqBy } from "lodash";
import { useMemo, useState } from "react";
import {
  CitationFragmentFragment,
  GetAskAiCitationSourcesQueryQuery,
  GetAskAiCitationSourcesQueryQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Avatars from "@components/avatar/avatars";
import IntegrationLogo from "@components/integration-logo/integration-logo";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { pluralize, titleCase } from "@helpers/string";

import getAskAiCitationSourcesQuery from "../graphql/get-ask-ai-citation-sources-query";
import AskAICitationRow from "./ask-ai-citation-row";

/** Minimal citation shape: at least title/url/sourceId; remaining fields are optional (hydrated via GQL). */
type MinimalCitation = {
  title: string;
  url: string;
  sourceId: string;
  sourceProvider?: string | null;
  sourceType?: string | null;
  contextText?: string | null;
};

const AskAICitationModal = ({
  citations,
  chatSessionId,
  onClose,
}: {
  citations: MinimalCitation[];
  chatSessionId?: number;
  onClose: () => void;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const reviewSourceProvider = label("review", { pluralize: true });
  const [filteredType, setFilteredType] = useState<string | null>(null);
  const sourceIds = useMemo(
    () => compact(citations.map((citation) => citation?.sourceId)),
    [citations],
  );
  const { data: hydratedCitationData, loading: loadingCitations } = useQuery<
    GetAskAiCitationSourcesQueryQuery,
    GetAskAiCitationSourcesQueryQueryVariables
  >(getAskAiCitationSourcesQuery, {
    variables: { chatSessionId: chatSessionId || 0, sourceIds },
    skip: !chatSessionId || sourceIds.length === 0,
    fetchPolicy: "cache-first",
  });
  const hydratedCitationsBySourceId = useMemo(
    (): Record<string, CitationFragmentFragment> =>
      Object.fromEntries(
        compact(hydratedCitationData?.askAiCitationSources || []).map(
          (citation) => [citation.sourceId, citation],
        ),
      ),
    [hydratedCitationData],
  );
  const compactedCitations = useMemo(
    () =>
      compact(citations).map((citation) => {
        const hydratedCitation =
          hydratedCitationsBySourceId[citation.sourceId] || citation;
        return {
          ...hydratedCitation,
          sourceProvider:
            hydratedCitation.sourceProvider === "topicflow" &&
            hydratedCitation.sourceType === "past_review"
              ? reviewSourceProvider
              : hydratedCitation.sourceProvider,
        };
      }),
    [
      citations,
      hydratedCitationsBySourceId,
      reviewSourceProvider,
      organization,
    ],
  );
  const citationsWithUniqueSourceProviders = useMemo(
    () =>
      uniqBy(compactedCitations, "sourceProvider").filter(
        (type) => type.sourceProvider,
      ),
    [compactedCitations],
  );
  const filteredCitations = useMemo(
    () =>
      compactedCitations.filter((citation) =>
        filteredType ? citation.sourceProvider === filteredType : true,
      ),
    [compactedCitations, filteredType],
  );

  const filterTypeClassName =
    "border px-3 py-1 rounded-full text-gray-500 flex items-center gap-1";
  const filterTypeClassNameActive = "border-blue-100 bg-blue-50";
  const maxAvatarsForReviews = 2;
  const coupleAvatarsForReviews = take(
    compact(
      compactedCitations
        .filter((citation) => citation.sourceProvider === reviewSourceProvider)
        .map((citation) => citation.assessment?.responder),
    ),
    maxAvatarsForReviews,
  );

  return (
    <Modal alignedTop open>
      <div className="p-6 flex flex-col gap-6">
        <ModalTitle onClose={onClose}>
          {compactedCitations.length}{" "}
          {pluralize("source", compactedCitations.length)} Used
        </ModalTitle>
        {!loadingCitations && citationsWithUniqueSourceProviders.length > 0 && (
          <div className="flex gap-2">
            <button
              className={classNames(
                filterTypeClassName,
                filteredType === null ? filterTypeClassNameActive : "",
              )}
              onClick={() => setFilteredType(null)}
            >
              All
            </button>
            {citationsWithUniqueSourceProviders.map((sourceProvider) => (
              <button
                key={sourceProvider.sourceProvider}
                className={classNames(
                  filterTypeClassName,
                  filteredType === sourceProvider.sourceProvider
                    ? filterTypeClassNameActive
                    : "",
                )}
                onClick={() =>
                  setFilteredType(sourceProvider.sourceProvider || null)
                }
              >
                {sourceProvider.sourceProvider === reviewSourceProvider ? (
                  <Avatars
                    users={coupleAvatarsForReviews}
                    max={maxAvatarsForReviews + 1}
                    className="pl-[2px]"
                    size={4}
                    clickingWillShowModal={false}
                    showTooltip={false}
                  />
                ) : sourceProvider.sourceProvider ? (
                  <IntegrationLogo
                    provider={sourceProvider.sourceProvider}
                    className="mt-0.5 w-4 h-4"
                  />
                ) : (
                  <TopicflowIcon
                    type="externalLink"
                    className="mt-0.5 w-4 h-4"
                  />
                )}
                {titleCase(
                  sourceProvider.sourceProvider?.replace(/^merge_/, ""),
                )}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-4">
          {loadingCitations
            ? Array.from({ length: citations.length }, (_, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <div className="bg-gray-100 animate-pulse rounded-full w-4 h-4 mt-0.5 shrink-0" />
                  <div className="bg-gray-100 animate-pulse rounded-md h-4 flex-1" />
                </div>
              ))
            : null}
          {!loadingCitations &&
            filteredCitations.map((citation) => (
              <AskAICitationRow
                key={citation.sourceId || citation.url}
                citation={citation}
                onNavigate={onClose}
              />
            ))}
          {!loadingCitations && filteredCitations.length === 0 && (
            <div className="text-gray-500">No citations found</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AskAICitationModal;
